import React, { Component, ReactElement, ReactNode, Suspense } from 'react'
import type { FallbackRender } from '@sentry/react'

interface Props {
  fallback: FallbackRender | ReactElement
  showDialog?: boolean
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
  SentryErrorBoundary: React.ComponentType<any> | null
}

/**
 * Lazy-loaded error boundary that dynamically loads Sentry's ErrorBoundary
 * Falls back to a basic error boundary if Sentry fails to load
 */
export class LazyErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      SentryErrorBoundary: null,
    }
  }

  async componentDidMount() {
    // Lazily load Sentry's ErrorBoundary
    try {
      const sentry = await import('@sentry/react')
      this.setState({ SentryErrorBoundary: sentry.ErrorBoundary })
    } catch (error) {
      console.error('Failed to load Sentry ErrorBoundary:', error)
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo })
    console.error('Error caught by boundary:', error, errorInfo)
  }

  render() {
    const { SentryErrorBoundary, hasError, error, errorInfo } = this.state
    const { fallback, showDialog, children } = this.props

    // If Sentry ErrorBoundary is loaded, use it
    if (SentryErrorBoundary) {
      return (
        <SentryErrorBoundary fallback={fallback} showDialog={showDialog}>
          {children}
        </SentryErrorBoundary>
      )
    }

    // Fallback error boundary behavior if Sentry hasn't loaded yet
    if (hasError && error) {
      if (typeof fallback === 'function') {
        return fallback({
          error,
          componentStack: errorInfo?.componentStack ?? '',
          eventId: '',
          resetError: () => this.setState({ hasError: false, error: null, errorInfo: null }),
        })
      }
      return fallback
    }

    return <>{children}</>
  }
}

export default LazyErrorBoundary
